<template>
    <div id="body" class="body-project">
        <div id="portfolio-feature-wrap">
            <div id="portfolio-feature">
                <div id="frame" class="frame frame-project">
                    <Header :mobile="platform.mobile"/>
                    <div class="body">
                        <Project
                            :project="project"
                            :selectedDetail="selectedDetail"
                            :timelineProjectMapID="timelineProjectMapID"
                            :mobile="platform.mobile"
                            @content-initiated="SetContentInitiated($event)"
                            @select-detail-button="SelectDetailButton($event)"
                        />
                    </div>
                </div>
                <Timeline
                    v-if="ShowAttachedTimeline()"
                    :timeline="timeline"
                    @navigate="ASSETS.Timeline.Navigation.Navigate(this, $event)"
                    @configure="ASSETS.Timeline.Configure(this, $event)"
                    @project="SetProject($event, true)"
                    @max-topography="SetTimelineProjectsMaxTopography($event)"
                />
            </div>
            <ProjectViewHistory :viewed="viewedProjects" @project="SetProject($event, false)"/>
        </div>
        <Timeline
            v-if="ShowDetachedTimeline()"
            :timeline="timeline"
            @navigate="ASSETS.Timeline.Navigation.Navigate(this, $event)"
            @configure="ASSETS.Timeline.Configure(this, $event)"
            @project="SetProject($event, true)"
            @max-topography="SetTimelineProjectsMaxTopography($event)"
        />

        <!-- Non-timeline project view -->
        <ProjectList
            v-if="projectViewType.view === 'List' || platform.mobile"
            :projects="projectList"
            :mobile="platform.mobile"
            :selectedProject="timeline.selectedProject"
            @project="SetProject($event)"
        />

        <Settings :topography="timelineProjectsMaxTopography" @select="SETTINGS.Select(this, $event)"/>
    </div>
</template>

<script>
import {
    viewModel,
    timelineModel,
    settingsModel,
    searchModel
} from "@/models/";
import { READ } from "@/data/Read.js";
import {
    ASSETS,
    PROJECT_VIEW_TYPES,
    SETTINGS
} from "@/assets/JS/Assets.js";
import { CONFIG } from "@/assets/JS/Config.js";
import { TimelineInstance } from "@/assets/JS/Timeline.js";
import { MASTER } from "@/assets/JS/Master.js";
import { Animation } from "@/assets/JS/Animation.js";
import DataAPI from "@/data/API.js";
import Navigation from "@/assets/JS/Data/Navigation.json";
import Header from "@/components/Template/Header.vue";
import Timeline from "@/components/Timeline/Timeline.vue";
import Project from "@/components/Projects/Project/Project.vue";
import ProjectViewHistory from "@/components/Projects/ViewHistory.vue";
import ProjectList from "@/components/Projects/CategoryList/Projects.vue";
import Settings from "@/components/Settings/Settings.vue";
import store from "@/store";
import $ from "jquery";

export default {
    name: "ProjectView",
    props: { ...viewModel.props },
    components: {
        Header,
        Timeline,
        Project,
        ProjectViewHistory,
        ProjectList,
        Settings
    },
    data: () => ({
        project: {},
        ...timelineModel(CONFIG),
        ASSETS,
        selectedDetail: {
            selection: "",
            contentInitiated: {}
        },
        timelineProjectMapID: 0,
        viewedProjects: [],
        ...viewModel.data,
        ...settingsModel.data,
        ...searchModel.data,
        timelineProjectsMaxTopography: 0,
        SETTINGS
    }),
    methods: {
        SetProject: async function(project, addToViewed) {
            this.$emit("start-loader", "#app");
            const { projectID, timelineProjectMapID, name } = project;
            document.body.scrollTop = document.documentElement.scrollTop = 0;
            this.project = await READ.ProjectByProjectID(this, DataAPI, projectID);
            this.project.YearRange = this.YearRange(timelineProjectMapID);
            this.selectedDetail.contentInitiated = {
                Types: false,
                Directives: false,
                Process: false
            };
            this.timeline.selectedProject = {
                id: projectID,
                timelineMapID: timelineProjectMapID,
                yearRange: this.project.YearRange
            };
            this.timeline.configurations.enabled = true;
            this.timelineProjectMapID = timelineProjectMapID;

            // Add to viewed list to act as breadcrumb of previously visited projects
            if (addToViewed) {
                this.viewedProjects.push({
                    projectID,
                    name,
                    timelineProjectMapID: this.timelineProjectMapID
                });
            }

            this.$emit("stop-loader");
        },
        SetContentInitiated: function(contentName) {
            this.selectedDetail.contentInitiated[contentName] = true;
        },
        SelectDetailButton: function(buttonName) { this.selectedDetail.selection = buttonName; },
        YearRange: function(timelineProjectMapID) {
            const timelineData = this.project.Timeline.filter(t => t.TimelineProjectMapID === timelineProjectMapID)[0];
            const { Start, End } = timelineData;
            return {
                start: new Date(Start).getFullYear(),
                end: new Date(End).getFullYear()
            };
        },
        ToggleProjectView: function(view) {
            this.projectViewType = {
                view,
                show: true
            };
            store.state.projectViewType = this.projectViewType;
            ASSETS.SetStorage("Portfolio", store.state);
        },
        ShowAttachedTimeline: function() {
            return (this.timeline.years && this.timeline.configurations.attached) &&
                !this.platform.mobile &&
                this.timeline.data;
        },
        ShowDetachedTimeline: function() {
            const {
                years,
                configurations,
                data,
                projectTypes,
                distinctProjectTypes
            } = this.timeline;
            const projectTypesLoaded = projectTypes.length > 0 && distinctProjectTypes.length > 0;
            return years &&
                !configurations.attached &&
                this.projectViewType.view === "Timeline" &&
                !this.platform.mobile &&
                data &&
                projectTypesLoaded;
        },
        SetTimelineProjectsMaxTopography: function(maxTopography) {
            this.timelineProjectsMaxTopography = maxTopography + 100;
        }
    },
    mounted: async function() {
        ASSETS.Construct($, Animation);
        this.$emit("start-loader", "#app");
        this.platform.mobile = MASTER.Platform._mobile;

        // Get project
        const portfolio = ASSETS.GetStorage("Portfolio");
        let projectID, timelineProjectMapID;
        if (portfolio !== null) {
            projectID = portfolio.projectID;
        } else {
            projectID = this.$route.meta.id;
        }
        timelineProjectMapID = this.$route.meta.timelineProjectMapID;
        if (projectID === -1) {
            projectID = portfolio.projectID;
        }
        if (timelineProjectMapID === -1) {
            timelineProjectMapID = portfolio.timelineProjectMapID;
        }

        // Set project view type
        if (portfolio.settings.projectViewType.view === "") {
            portfolio.settings.projectViewType.view = PROJECT_VIEW_TYPES.timeline;
        }
        store.state.projectViewType = portfolio.settings.projectViewType;
        this.projectViewType = ASSETS.Projects.ViewType(portfolio.settings);

        this.project = await READ.ProjectByProjectID(this, DataAPI, projectID);
        this.project.YearRange = this.YearRange(timelineProjectMapID);
        this.timeline.selectedProject = {
            id: projectID,
            timelineMapID: timelineProjectMapID,
            yearRange: this.project.YearRange
        };
        this.timelineProjectMapID = timelineProjectMapID;

        // Get timeline data
        const api = {
            READ,
            DataAPI
        };
        if (!this.platform.mobile) {
            await ASSETS.Timeline.Get(
                this,
                {
                    READ,
                    DataAPI
                },
                TimelineInstance,
                Navigation.Projects.PriorityTypes,
                CONFIG
            );
            await ASSETS.Timeline.Load(
                this,
                false,
                {
                    READ,
                    DataAPI
                },
                store,
                projectID,
                timelineProjectMapID
            );
        } else {
            await ASSETS.Mobile.LoadProjects(this, api, store, projectID, timelineProjectMapID);
        }

        this.timeline.priority.selected = true;
        this.timeline.configurations.enabled = true;
        this.$emit("timeline-configurations", this.timeline.configurations);
        this.timeline.priority.counts = await READ.Counts(this, DataAPI);
        this.timeline.distinctProjectTypes = await READ.DistinctTypes(this, DataAPI);
        this.timeline.projectTypes = await READ.ProjectTypes(this, DataAPI);
        this.$emit("stop-loader");
    },
    emits: [ ...viewModel.emits ]
};
</script>